import type { ReactNode } from "react";

import { cn } from "../ui/cn";

// The fixed pieces of the shell frame, each small enough to read at once: the banner of the development identity, the sidebar frame and
// the main region. `ProtectedLayout` owns the data and the state; these only draw.

/** The window of the shell: the sidebar colour around the panel on a wide screen, and the whole window for a full-bleed workspace. */
export function ShellFrame({
  fullBleed,
  children,
}: Readonly<{ fullBleed: boolean; children: ReactNode }>) {
  return (
    <div
      className={cn(
        "flex min-h-svh bg-background text-foreground md:gap-2 md:bg-sidebar md:p-2",
        fullBleed ? "h-dvh min-h-0 overflow-hidden" : undefined,
      )}
    >
      {children}
    </div>
  );
}

/** A region with a fixed name, so a screen reader finds it and no content sits outside a landmark. */
export function DevelopmentIdentityBanner() {
  return (
    <section
      aria-label="Development identity"
      className="border-b border-warning-border bg-banner px-4 py-2.5 text-sm leading-6 text-banner-foreground md:px-6"
    >
      Development identity placeholder. Navigation role is presentation-only and is not trusted
      authorization. Staging and production use server-authoritative Supabase identity and trusted
      staff grants.
    </section>
  );
}

export function SkipLink() {
  return (
    <a
      className="skip-link fixed top-2 left-2 z-[var(--lf-z-toast)] -translate-y-[160%] rounded-sm bg-card px-3.5 py-2.5 text-card-foreground shadow-lg focus:translate-y-0"
      href="#main-content"
    >
      Skip to main content
    </a>
  );
}

export function DesktopSidebar({
  collapsed,
  children,
}: Readonly<{ collapsed: boolean; children: ReactNode }>) {
  return (
    <aside
      id="app-sidebar"
      aria-label="Staff console sidebar"
      data-collapsed={collapsed ? "true" : "false"}
      className={cn(
        "sticky top-2 hidden h-[calc(100svh-1rem)] shrink-0 bg-sidebar text-sidebar-foreground transition-[width] duration-200 md:block",
        collapsed ? "w-16" : "w-58",
      )}
    >
      {children}
    </aside>
  );
}

/** The rounded panel beside the sidebar. A full-bleed workspace clips to its corners and scrolls inside its own panes. */
export function PagePanel({
  fullBleed,
  children,
}: Readonly<{ fullBleed: boolean; children: ReactNode }>) {
  return (
    <div
      className={cn(
        "flex min-w-0 flex-1 flex-col bg-background md:min-h-[calc(100svh-1rem)] md:rounded-[20px] md:border md:border-border",
        fullBleed && "min-h-0 md:overflow-hidden",
      )}
    >
      {children}
    </div>
  );
}

export function MainRegion({
  wide,
  fullBleed,
  children,
}: Readonly<{ wide: boolean; fullBleed: boolean; children: ReactNode }>) {
  return (
    <main
      id="main-content"
      tabIndex={-1}
      className={cn(
        "mx-auto w-full flex-1 [overflow-wrap:break-word]",
        fullBleed ? "min-h-0 p-0" : "p-4 sm:p-6 lg:p-8",
        fullBleed || wide ? "max-w-none" : "max-w-[1080px]",
      )}
    >
      {children}
    </main>
  );
}
